import { useState } from 'react';
import { getDistanceUnit, distanceLabelFull, milesToDisplay, displayToMiles } from '../../utils/units';

/**
 * Edit vehicle form — pre-filled with current data.
 * Shows all editable fields and a "Delete Vehicle" button at bottom.
 */
export default function EditVehicle({ vehicle, onSave, onBack, onDelete, onToast, canFull }) {
  const initialUnit = vehicle.distance_units || getDistanceUnit();
  const [form, setForm] = useState({
    year: vehicle.year || '',
    make: vehicle.make || '',
    model: vehicle.model || '',
    trim: vehicle.trim || '',
    // Convert stored miles → display value for the vehicle's unit
    mileage: vehicle.mileage ? String(milesToDisplay(vehicle.mileage, initialUnit)) : '',
    tmu: vehicle.tmu || false,
    condition: vehicle.condition || '',
    primaryUse: vehicle.primaryUse || '',
    vin: vehicle.vin || '',
    distance_units: initialUnit,
  });

  function update(fields) {
    setForm((prev) => ({ ...prev, ...fields }));
  }

  function handleUnitChange(newUnit) {
    // Convert displayed mileage from current unit to new unit
    if (form.mileage && form.distance_units !== newUnit) {
      const milesValue = displayToMiles(parseInt(form.mileage, 10), form.distance_units);
      const convertedDisplay = milesToDisplay(milesValue, newUnit);
      update({ distance_units: newUnit, mileage: convertedDisplay != null ? String(convertedDisplay) : '' });
    } else {
      update({ distance_units: newUnit });
    }
  }

  function handleSave() {
    if (!form.year || !form.make || !form.model) {
      onToast?.('Year, Make, and Model are required');
      return;
    }
    // Convert display value back to internal miles for storage
    const milesValue = form.mileage ? displayToMiles(parseInt(form.mileage, 10), form.distance_units) : form.mileage;
    onSave({ ...vehicle, ...form, mileage: milesValue });
  }

  const CONDITIONS = ['Excellent', 'Good', 'Fair', 'Poor'];
  const unitLabel = distanceLabelFull(form.distance_units);

  return (
    <div className="screen" style={{ paddingBottom: 100, overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          onClick={onBack}
          style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'var(--bg-elevated)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            border: 'none', cursor: 'pointer', flexShrink: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="screen-title" style={{ fontSize: '1.25rem' }}>Edit Vehicle</h1>
      </div>

      {/* Year / Make / Model */}
      <div className="form-group">
        <label className="form-label">Year</label>
        <input
          className="form-input"
          type="number"
          value={form.year}
          onChange={(e) => update({ year: e.target.value })}
          placeholder="2024"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Make</label>
        <input
          className="form-input"
          type="text"
          value={form.make}
          onChange={(e) => update({ make: e.target.value })}
          placeholder="Toyota"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Model</label>
        <input
          className="form-input"
          type="text"
          value={form.model}
          onChange={(e) => update({ model: e.target.value })}
          placeholder="Camry"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Trim (optional)</label>
        <input
          className="form-input"
          type="text"
          value={form.trim}
          onChange={(e) => update({ trim: e.target.value })}
          placeholder="SE, XLE, TRD..."
        />
      </div>

      {/* Distance Units Toggle */}
      <div className="form-group">
        <label className="form-label">Distance Units</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {['miles', 'km'].map((unit) => (
            <button
              key={unit}
              onClick={() => handleUnitChange(unit)}
              style={{
                padding: '8px 20px',
                borderRadius: 100,
                fontSize: '0.8125rem',
                fontWeight: 600,
                border: `1.5px solid ${form.distance_units === unit ? 'var(--accent)' : 'var(--border)'}`,
                background: form.distance_units === unit ? 'var(--accent-subtle)' : 'transparent',
                color: form.distance_units === unit ? 'var(--accent)' : 'var(--text-secondary)',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {unit === 'miles' ? 'Miles' : 'Km'}
            </button>
          ))}
        </div>
      </div>

      {/* Mileage */}
      <div className="form-group">
        <label className="form-label">{`Current Odometer Reading (${unitLabel})`}</label>
        <input
          className="form-input"
          type="number"
          inputMode="numeric"
          value={form.mileage}
          onChange={(e) => update({ mileage: e.target.value })}
          placeholder={form.distance_units === 'km' ? 'e.g. 39,400' : 'e.g. 24,500'}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.tmu}
            onChange={(e) => update({ tmu: e.target.checked })}
            style={{ width: 16, height: 16, accentColor: 'var(--accent)' }}
          />
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            Total Mileage Unknown (TMU)
          </span>
        </label>
      </div>

      {/* VIN */}
      <div className="form-group">
        <label className="form-label">VIN (optional)</label>
        <input
          className="form-input"
          type="text"
          value={form.vin}
          onChange={(e) => update({ vin: e.target.value.toUpperCase() })}
          placeholder="1HGBH41JXMN109186"
          maxLength={17}
          style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}
        />
      </div>

      {/* Condition */}
      <div className="form-group">
        <label className="form-label">Condition</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {CONDITIONS.map((c) => (
            <button
              key={c}
              onClick={() => update({ condition: c })}
              style={{
                padding: '8px 16px',
                borderRadius: 100,
                fontSize: '0.8125rem',
                fontWeight: 600,
                border: `1.5px solid ${form.condition === c ? 'var(--accent)' : 'var(--border)'}`,
                background: form.condition === c ? 'var(--accent-subtle)' : 'transparent',
                color: form.condition === c ? 'var(--accent)' : 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <button
        className="btn btn-primary"
        onClick={handleSave}
        style={{ marginTop: 12, marginBottom: 32 }}
      >
        Save Changes
      </button>

      {/* Delete Vehicle — requires FULL_ACCESS */}
      {canFull !== false && (
        <>
          <div style={{ height: 1, background: 'var(--border)', margin: '8px 0 24px' }} />
          <button
            onClick={onDelete}
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: 'var(--radius-sm)',
              border: '1.5px solid var(--danger)',
              color: 'var(--danger)',
              fontSize: '0.875rem',
              fontWeight: 600,
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            Remove Vehicle
          </button>
        </>
      )}
    </div>
  );
}
